import { useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Button } from "../ui/button";
import { UploadSimple, Sparkle, FileText, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const PHASES = {
  queued: "Queued…",
  reading: "Reading documents (OCR for scans)…",
  header: "Identifying client, equipment & standards…",
  extract: "Extracting technical parameters…",
  infer: "Proposing standards-based defaults…",
};

/** Upload tender/spec documents before the project exists and let Cognara AI fill the form. */
export default function AutofillDrop({ onResult, disabled }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list)].slice(0, 8);
    setFiles(next);
  };

  const run = async () => {
    if (!files.length) return;
    setBusy(true);
    setStep({ phase: "queued" });
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/autofill/prefill", fd);
      for (;;) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: job } = await api.get(`/autofill/jobs/${data.job_id}`);
        setStep(job.step);
        if (job.status === "done") {
          onResult(job.result);
          const s = job.result.summary;
          toast.success(`Autofilled — ${s.extracted} extracted, ${s.inferred} AI-inferred, ${s.missing} open`);
          setFiles([]);
          break;
        }
        if (job.status === "error") throw new Error(job.error || "Autofill failed");
      }
    } catch (e) {
      toast.error(fmtErr(e), { duration: 8000 });
    } finally {
      setBusy(false);
      setStep(null);
    }
  };

  return (
    <div data-testid="autofill-drop"
      className="relative overflow-hidden rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--c-accent)] to-transparent opacity-70" />
      <div className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Sparkle size={15} className="text-[var(--c-accent)]" />
              <span className="text-sm font-medium text-[var(--c-text)]">Autofill from tender / specification</span>
            </div>
            <p className="text-xs text-[var(--c-muted)] mt-1.5 max-w-xl leading-relaxed">
              Drop the enquiry in any format — PDF, Word, Excel, text, scanned PDF or a photo of the spec sheet.
              Cognara AI reads it and fills the commercial header, equipment type, standards basis and every
              technical parameter, with the source quote behind each value.
            </p>
          </div>
          <Button data-testid="autofill-run" onClick={run} disabled={busy || disabled || !files.length}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent)]/85 text-white rounded-sm shrink-0">
            <Sparkle size={14} className="mr-1.5" /> {busy ? "Reading…" : "Autofill"}
          </Button>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          data-testid="autofill-dropzone"
          className={`mt-4 cursor-pointer rounded-lg border border-dashed px-4 py-6 text-center transition-colors duration-200
            ${drag ? "border-[var(--c-accent)] bg-[var(--c-accent)]/5" : "border-[var(--c-border)] hover:border-[var(--c-accent)]/60"}`}>
          <UploadSimple size={20} className="mx-auto text-[var(--c-muted)]" />
          <div className="text-xs text-[var(--c-text)] mt-2">Drop files here or click to browse</div>
          <div className="text-[10px] text-[var(--c-muted)] mt-1 font-mono">pdf · docx · xlsx · csv · txt · png · jpg — up to 8 files, 30 MB each</div>
          <input ref={inputRef} type="file" multiple hidden data-testid="autofill-file-input"
            onChange={(e) => addFiles(e.target.files)} />
        </div>

        {files.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="autofill-file-list">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--c-border)] bg-[var(--c-bg)] pl-2.5 pr-1.5 py-1 text-[11px] text-[var(--c-text)]">
                <FileText size={12} className="text-[var(--c-muted)]" /> {f.name}
                <button data-testid={`autofill-remove-${i}`} onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, j) => j !== i)); }}
                  className="rounded-full p-0.5 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}

        {busy && (
          <div className="mt-4" data-testid="autofill-progress">
            <div className="flex items-center justify-between text-[10px] font-mono text-[var(--c-muted)] mb-1.5">
              <span>{PHASES[step?.phase] || "Working…"}</span>
              <span>{step?.detail || ""}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-[var(--c-border)]">
              <div className="h-full w-1/3 animate-[slide_1.4s_ease-in-out_infinite] bg-[var(--c-accent)]" />
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes slide{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}`}</style>
    </div>
  );
}
